/**
 * Reusable "Continue with Google" / "Continue with Microsoft" buttons.
 *
 * Each button is a full-width inline-styled button matching the existing
 * Button primitive in /components/ui/Button.tsx. We deliberately avoid
 * importing Google's brand assets to keep the bundle dependency-free; the
 * inline SVG glyphs below are the official Material/Microsoft simplified
 * marks (public-domain shapes — not the trademark logo lock-up).
 *
 * The buttons are rendered conditionally based on feature flags so a
 * misconfigured provider does not show a 503 error to the user.
 */
import type { CSSProperties, ReactNode } from 'react';
import { startOAuthSignIn } from '../../lib/auth';
import { getAuthFeatureFlags } from '../../lib/featureFlags';

const BTN: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  width: '100%',
  padding: '0.6rem 1rem',
  border: '1px solid #cbd5e1',
  background: 'white',
  color: '#1f2937',
  borderRadius: '0.25rem',
  cursor: 'pointer',
  fontSize: '0.95rem',
  fontWeight: 500,
  marginBottom: '0.5rem',
};

function ProviderButton(props: {
  label: string;
  testId: string;
  onClick: () => void;
  icon: ReactNode;
}) {
  return (
    <button type="button" data-testid={props.testId} onClick={props.onClick} style={BTN}>
      <span aria-hidden style={{ width: 18, height: 18, display: 'inline-flex' }}>
        {props.icon}
      </span>
      <span>{props.label}</span>
    </button>
  );
}

// Simplified vector marks — solid colour squares that hint at the provider
// without using the brand-protected logo. Operators may later swap these
// for the official SVG marks via the brand guidelines.
const GoogleGlyph = (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    <path
      fill="#4285F4"
      d="M21.6 12.2c0-.7-.1-1.3-.2-2H12v3.8h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.3z"
    />
    <path
      fill="#34A853"
      d="M12 22c2.7 0 5-1 6.6-2.5l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.7-5.6-4.1H3v2.6A10 10 0 0 0 12 22z"
    />
    <path fill="#FBBC04" d="M6.4 13.9a6 6 0 0 1 0-3.8V7.5H3a10 10 0 0 0 0 9z" />
    <path
      fill="#EA4335"
      d="M12 5.9c1.5 0 2.8.5 3.8 1.5l2.9-2.9A10 10 0 0 0 3 7.5l3.4 2.6c.8-2.4 3-4.2 5.6-4.2z"
    />
  </svg>
);

const MicrosoftGlyph = (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    <rect x="2" y="2" width="9.5" height="9.5" fill="#F35325" />
    <rect x="12.5" y="2" width="9.5" height="9.5" fill="#81BC06" />
    <rect x="2" y="12.5" width="9.5" height="9.5" fill="#05A6F0" />
    <rect x="12.5" y="12.5" width="9.5" height="9.5" fill="#FFBA08" />
  </svg>
);

export interface OAuthButtonsProps {
  /** Same-origin path the user lands on after a successful sign-in. */
  returnTo?: string;
  /**
   * Flow intent sealed into the OAuth state. 'login' (default) never
   * provisions a new account; only registration pages pass 'register'.
   */
  intent?: 'login' | 'register';
  /** Override the feature flags (test seam). */
  flags?: { googleOAuth: boolean; microsoftOAuth: boolean };
}

export function OAuthButtons(props: OAuthButtonsProps) {
  const flags = props.flags ?? getAuthFeatureFlags();
  const intent = props.intent ?? 'login';
  if (!flags.googleOAuth && !flags.microsoftOAuth) return null;
  return (
    <div style={{ marginBottom: '1rem' }}>
      {flags.googleOAuth ? (
        <ProviderButton
          testId="oauth-google-btn"
          label="Continue with Google"
          icon={GoogleGlyph}
          onClick={() => startOAuthSignIn('google', props.returnTo, intent)}
        />
      ) : null}
      {flags.microsoftOAuth ? (
        <ProviderButton
          testId="oauth-microsoft-btn"
          label="Continue with Microsoft"
          icon={MicrosoftGlyph}
          onClick={() => startOAuthSignIn('microsoft', props.returnTo, intent)}
        />
      ) : null}
    </div>
  );
}

/** Horizontal divider used between OAuth buttons and the email form. */
export function AuthDivider(props: { label?: string }) {
  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        margin: '0.5rem 0 1rem',
        color: '#666',
        fontSize: '0.85rem',
      }}
    >
      <span style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
      <span>{props.label ?? 'or'}</span>
      <span style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
    </div>
  );
}
